import LegalPageLayout, { Section, SubSection, Paragraph, List } from '../../components/LegalPageLayout'

export default function TermsOfServicePage() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="February 1, 2026">
      {/* Agreement to Terms */}
      <Section title="1. Agreement to Terms">
        <Paragraph>
          By accessing or using DIZEE ("Service," "Platform," "we," "us," or "our"), you agree to be bound by these Terms
          of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
        </Paragraph>
        <Paragraph>
          We reserve the right to modify these Terms at any time. Your continued use of the Service after changes are
          posted constitutes acceptance of the modified Terms.
        </Paragraph>
      </Section>

      {/* Use of Service */}
      <Section title="2. Use of Service">
        <SubSection title="2.1 Eligibility">
          <Paragraph>
            You must be at least 13 years old to use this Service. If you are under 18, you must have permission from a
            parent or guardian. By using the Service, you represent that you meet these requirements.
          </Paragraph>
        </SubSection>

        <SubSection title="2.2 Account Registration">
          <Paragraph>To access certain features, you must register for an account. You agree to:</Paragraph>
          <List
            items={[
              'Provide accurate, current, and complete information',
              'Maintain the security of your account credentials',
              'Notify us immediately of any unauthorized access',
              'Accept responsibility for all activities under your account',
            ]}
          />
        </SubSection>

        <SubSection title="2.3 License to Use">
          <Paragraph>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access
            and use the Service for your personal or business purposes.
          </Paragraph>
        </SubSection>
      </Section>

      {/* Prohibited Activities */}
      <Section title="3. Prohibited Activities">
        <Paragraph>You agree not to:</Paragraph>
        <List
          items={[
            'Use the Service for any illegal purpose',
            'Attempt to gain unauthorized access to our systems',
            'Interfere with or disrupt the Service or servers',
            'Use automated tools (bots, scrapers) without permission',
            'Reverse engineer or decompile any part of the Service',
            'Impersonate another person or entity',
            'Collect user information without consent',
            'Transmit spam or unsolicited communications',
          ]}
        />
      </Section>

      {/* User Content */}
      <Section title="4. User Content">
        <SubSection title="4.1 Your Content">
          <Paragraph>
            You retain ownership of any content you submit, post, or display on the Service ("User Content"). By
            submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce,
            modify, and distribute your content in connection with providing the Service.
          </Paragraph>
        </SubSection>

        <SubSection title="4.2 Content Standards">
          <Paragraph>You are solely responsible for your User Content. You agree not to post content that:</Paragraph>
          <List
            items={[
              'Violates any law or regulation',
              'Infringes on intellectual property rights',
              'Contains harmful, threatening, or harassing material',
              'Contains viruses or malicious code',
              'Impersonates any person or entity',
            ]}
          />
        </SubSection>
      </Section>

      {/* Intellectual Property */}
      <Section title="5. Intellectual Property Rights">
        <Paragraph>
          The Service and its original content, features, and functionality are owned by DIZEE and are protected by
          international copyright, trademark, patent, trade secret, and other intellectual property laws.
        </Paragraph>
        <Paragraph>
          You may not copy, modify, distribute, sell, or lease any part of our Service without our express written
          permission.
        </Paragraph>
      </Section>

      {/* Payment Terms */}
      <Section title="6. Payment and Subscriptions">
        <SubSection title="6.1 Fees">
          <Paragraph>
            Some features of the Service may require payment of fees. You agree to pay all fees associated with your
            account according to the pricing and payment terms presented to you at the time of purchase.
          </Paragraph>
        </SubSection>

        <SubSection title="6.2 Subscriptions">
          <Paragraph>
            Subscription fees are billed in advance on a recurring basis (monthly or annually). Your subscription will
            automatically renew unless you cancel it before the renewal date. You can cancel your subscription at any time
            through your account settings.
          </Paragraph>
        </SubSection>

        <SubSection title="6.3 Refunds">
          <Paragraph>
            All fees are non-refundable except where required by law or as expressly stated in these Terms.
          </Paragraph>
        </SubSection>
      </Section>

      {/* Disclaimer of Warranties */}
      <Section title="7. Disclaimer of Warranties">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-lg mt-4">
          <p className="text-xs uppercase font-semibold text-red-900 dark:text-red-300">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
          </p>
        </div>
        <Paragraph>We do not warrant that:</Paragraph>
        <List
          items={[
            'The Service will be uninterrupted, secure, or error-free',
            'The results obtained from the Service will be accurate or reliable',
            'Any errors in the Service will be corrected',
            'The Service will meet your specific requirements',
          ]}
        />
      </Section>

      {/* Limitation of Liability */}
      <Section title="8. Limitation of Liability">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-lg mt-4">
          <p className="text-xs uppercase font-semibold text-red-900 dark:text-red-300">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, DIZEE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
            CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.
          </p>
        </div>
        <Paragraph>
          Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you
          paid us in the 12 months prior to the claim, or $100, whichever is greater.
        </Paragraph>
      </Section>

      {/* Termination */}
      <Section title="9. Termination">
        <Paragraph>
          We may terminate or suspend your account and access to the Service immediately, without prior notice or
          liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Service
          will immediately cease.
        </Paragraph>
      </Section>

      {/* Governing Law */}
      <Section title="10. Governing Law">
        <Paragraph>
          These Terms are governed by the laws of the State of California, without regard to conflict of law principles. Any disputes shall be resolved in the courts of California.
        </Paragraph>
      </Section>

      {/* Contact Us */}
      <Section title="11. Contact Us">
        <Paragraph>If you have questions about these Terms, please contact us:</Paragraph>
        <div className="mt-4 ds-surface p-6">
          <p className="text-sm mb-3">
            <strong className="font-semibold">Email:</strong>{' '}
            <a
              href="mailto:contact@dizee.com"
              className="text-blue-600 hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
            >
              contact@dizee.com
            </a>
          </p>
          <p className="text-sm">
            <strong className="font-semibold">Address:</strong> ARTIST 360 INC, Los Angeles, California, USA
          </p>
        </div>
      </Section>
    </LegalPageLayout>
  )
}
